import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Image,
  FileText,
  Award,
  Mail,
  Plus,
  Edit,
  Trash2,
  Layout,
  Palette,
  ShieldAlert,
  Zap,
  Eye,
  Star,
} from 'lucide-react';

interface Rule {
  id: number;
  rule: string;
  enabled: boolean;
  severity: string;
}

interface GuidelineSection {
  label: string;
  icon: string;
  rules: Rule[];
}

interface Guidelines {
  [key: string]: GuidelineSection;
}

interface BrandingCompliance {
  enforceCompanyLogo: boolean;
  enforceColorScheme: boolean;
  allowCustomTemplates: boolean;
  requireLegalDisclaimer: boolean;
}

interface ProhibitedContentItem {
  id: number;
  category: string;
  keywords: string[];
  enabled: boolean;
}

interface Template {
  id: number;
  name: string;
  preview: string;
  available: boolean;
  default: boolean;
}

interface ContentGuidelinesTabProps {
  onSettingsChange: () => void;
}

const ContentGuidelinesTab = ({ onSettingsChange }: ContentGuidelinesTabProps) => {
  const { t } = useTranslation();

  const [guidelines, setGuidelines] = useState<Guidelines>({
    profilePhoto: {
      label: 'Profile Photo Guidelines',
      icon: 'Image',
      rules: [
        { id: 1, rule: 'Professional headshot required', enabled: true, severity: 'error' },
        { id: 2, rule: 'Minimum resolution: 400x400 pixels', enabled: true, severity: 'error' },
        { id: 3, rule: 'Maximum file size: 5MB', enabled: true, severity: 'error' },
        { id: 4, rule: 'Accepted formats: JPG, PNG', enabled: true, severity: 'error' },
        { id: 5, rule: 'No group photos or logos', enabled: true, severity: 'warning' },
      ],
    },
    bio: {
      label: 'Professional Bio Guidelines',
      icon: 'FileText',
      rules: [
        { id: 1, rule: 'Minimum 50 characters', enabled: true, severity: 'warning' },
        { id: 2, rule: 'Maximum 500 characters', enabled: true, severity: 'error' },
        { id: 3, rule: 'No personal contact information', enabled: true, severity: 'error' },
        { id: 4, rule: 'Professional tone required', enabled: true, severity: 'warning' },
        { id: 5, rule: 'No external links', enabled: true, severity: 'warning' },
      ],
    },
    skills: {
      label: 'Skills & Expertise Guidelines',
      icon: 'Award',
      rules: [
        { id: 1, rule: 'Minimum 3 skills required', enabled: true, severity: 'warning' },
        { id: 2, rule: 'Maximum 15 skills allowed', enabled: true, severity: 'error' },
        { id: 3, rule: 'Skills must be from approved list', enabled: false, severity: 'warning' },
        { id: 4, rule: 'Certifications require verification', enabled: true, severity: 'warning' },
      ],
    },
    contactInfo: {
      label: 'Contact Information Guidelines',
      icon: 'Mail',
      rules: [
        { id: 1, rule: 'Company email required', enabled: true, severity: 'error' },
        { id: 2, rule: 'Phone number format validation', enabled: true, severity: 'error' },
        { id: 3, rule: 'LinkedIn URL format validation', enabled: true, severity: 'warning' },
        { id: 4, rule: 'No personal email addresses', enabled: true, severity: 'warning' },
      ],
    },
  });

  const [brandingCompliance, setBrandingCompliance] = useState<BrandingCompliance>({
    enforceCompanyLogo: true,
    enforceColorScheme: true,
    allowCustomTemplates: false,
    requireLegalDisclaimer: true,
  });

  const [prohibitedContent, setProhibitedContent] = useState<ProhibitedContentItem[]>([
    { id: 1, category: 'Offensive Language', keywords: ['inappropriate', 'offensive'], enabled: true },
    { id: 2, category: 'Competitor Mentions', keywords: ['competitor1', 'competitor2'], enabled: true },
    { id: 3, category: 'Confidential Information', keywords: ['confidential', 'internal'], enabled: true },
    { id: 4, category: 'Political Content', keywords: ['political', 'partisan'], enabled: false },
  ]);

  const [templateAvailability, setTemplateAvailability] = useState<Template[]>([
    { id: 1, name: 'Modern Professional', preview: 'modern', available: true, default: true },
    { id: 2, name: 'Classic Business', preview: 'classic', available: true, default: false },
    { id: 3, name: 'Creative Portfolio', preview: 'creative', available: false, default: false },
    { id: 4, name: 'Minimal Executive', preview: 'minimal', available: true, default: false },
  ]);

  const handleToggleRule = (section: string, ruleId: number) => {
    setGuidelines((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        rules: prev[section].rules.map((rule) => (rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule)),
      },
    }));
    onSettingsChange();
  };

  const handleToggleBranding = (key: keyof BrandingCompliance) => {
    setBrandingCompliance((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    onSettingsChange();
  };

  const handleToggleProhibited = (id: number) => {
    setProhibitedContent((prev) => prev.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item)));
    onSettingsChange();
  };

  const handleToggleTemplate = (id: number) => {
    setTemplateAvailability((prev) =>
      prev.map((template) => (template.id === id ? { ...template, available: !template.available } : template))
    );
    onSettingsChange();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'warning':
        return 'text-amber-600 bg-amber-50';
      case 'info':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Image':
        return <Image className="w-[18px] h-[18px] text-gray-600 mr-2" />;
      case 'FileText':
        return <FileText className="w-[18px] h-[18px] text-gray-600 mr-2" />;
      case 'Award':
        return <Award className="w-[18px] h-[18px] text-gray-600 mr-2" />;
      case 'Mail':
        return <Mail className="w-[18px] h-[18px] text-gray-600 mr-2" />;
      default:
        return <FileText className="w-[18px] h-[18px] text-gray-600 mr-2" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Content Validation Rules */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Content Validation Rules')}</h3>
        <div className="space-y-4">
          {Object.entries(guidelines).map(([key, section]) => (
            <div key={key} className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="bg-muted px-4 py-3 border-b border-border">
                <div className="flex items-center">
                  {getIconComponent(section.icon)}
                  <h4 className="text-sm font-semibold text-gray-900">{t(section.label)}</h4>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-2">
                  {section.rules.map((rule) => (
                    <label
                      key={rule.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center flex-1">
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={() => handleToggleRule(key, rule.id)}
                          className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20 mr-3"
                        />
                        <span className="text-sm text-gray-900">{t(rule.rule)}</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(rule.severity)}`}>
                        {t(rule.severity)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Template Availability */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Layout className="w-5 h-5 text-gray-600 mr-2" />
            <h4 className="text-sm font-semibold text-gray-900">{t('Template Availability')}</h4>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
            <Plus className="w-4 h-4" />
            {t('Add Template')}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templateAvailability.map((template) => (
            <div
              key={template.id}
              className={`border rounded-lg p-4 transition-all ${
                template.available ? 'border-border bg-card' : 'border-border bg-muted opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">{t(template.name)}</h5>
                  {template.default && <span className="text-xs text-blue-600 font-medium">{t('Default Template')}</span>}
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={template.available}
                    onChange={() => handleToggleTemplate(template.id)}
                    className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20"
                  />
                </label>
              </div>
              <div className="h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
                <Layout className="w-8 h-8 text-blue-400" />
              </div>
              <div className="mt-3 flex gap-2">
                <button className="flex items-center justify-center gap-1 flex-1 px-2 py-1.5 text-xs border border-border rounded text-gray-700 hover:bg-gray-50">
                  <Eye className="w-3 h-3" />
                  {t('Preview')}
                </button>
                {!template.default && (
                  <button className="flex items-center justify-center gap-1 flex-1 px-2 py-1.5 text-xs border border-border rounded text-gray-700 hover:bg-gray-50">
                    <Star className="w-3 h-3" />
                    {t('Set Default')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Branding Compliance */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center mb-4">
          <Palette className="w-5 h-5 text-gray-600 mr-2" />
          <h4 className="text-sm font-semibold text-gray-900">{t('Branding Compliance Rules')}</h4>
        </div>

        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="text-sm font-medium text-gray-900">{t('Enforce Company Logo')}</p>
              <p className="text-xs text-gray-500">{t('All profiles must display company logo in header')}</p>
            </div>
            <input
              type="checkbox"
              checked={brandingCompliance.enforceCompanyLogo}
              onChange={() => handleToggleBranding('enforceCompanyLogo')}
              className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20"
            />
          </label>

          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="text-sm font-medium text-gray-900">{t('Enforce Color Scheme')}</p>
              <p className="text-xs text-gray-500">{t('Restrict profiles to company brand colors only')}</p>
            </div>
            <input
              type="checkbox"
              checked={brandingCompliance.enforceColorScheme}
              onChange={() => handleToggleBranding('enforceColorScheme')}
              className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20"
            />
          </label>

          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="text-sm font-medium text-gray-900">{t('Allow Custom Templates')}</p>
              <p className="text-xs text-gray-500">{t('Permit employees to create custom profile templates')}</p>
            </div>
            <input
              type="checkbox"
              checked={brandingCompliance.allowCustomTemplates}
              onChange={() => handleToggleBranding('allowCustomTemplates')}
              className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20"
            />
          </label>

          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="text-sm font-medium text-gray-900">{t('Require Legal Disclaimer')}</p>
              <p className="text-xs text-gray-500">{t('Display legal disclaimer on all public profiles')}</p>
            </div>
            <input
              type="checkbox"
              checked={brandingCompliance.requireLegalDisclaimer}
              onChange={() => handleToggleBranding('requireLegalDisclaimer')}
              className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20"
            />
          </label>
        </div>
      </div>

      {/* Prohibited Content Policies */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ShieldAlert className="w-5 h-5 text-gray-600 mr-2" />
            <h4 className="text-sm font-semibold text-gray-900">{t('Prohibited Content Policies')}</h4>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
            <Plus className="w-4 h-4" />
            {t('Add Policy')}
          </button>
        </div>

        <div className="space-y-3">
          {prohibitedContent.map((policy) => (
            <div
              key={policy.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                policy.enabled ? 'bg-red-50 border-red-200' : 'bg-muted border-border'
              }`}
            >
              <div className="flex items-center flex-1">
                <input
                  type="checkbox"
                  checked={policy.enabled}
                  onChange={() => handleToggleProhibited(policy.id)}
                  className="h-4 w-4 rounded border-border text-red-600 focus:ring-red-500 mr-3"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{t(policy.category)}</p>
                  <p className="text-xs text-gray-500">
                    {t('Keywords')}: {policy.keywords.join(', ')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="p-1 text-gray-700 hover:text-gray-900">
                  <Edit className="w-4 h-4" />
                </button>
                <button className="p-1 text-gray-700 hover:text-gray-900">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Automated Validation System */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Zap className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">{t('Automated Validation System')}</h4>
            <p className="text-sm text-blue-800 mb-3">
              {t(
                'All content guidelines are automatically validated in real-time when employees update their profiles. Violations trigger immediate feedback with suggestions for compliance.'
              )}
            </p>
            <div className="flex gap-2">
              <button className="px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
                {t('Test Validation Rules')}
              </button>
              <button className="px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
                {t('View Validation Logs')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentGuidelinesTab;
